import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react'
import { Pageable, ServiceRequestService, ServiceResendResponse } from '../../models'
import type { ServiceStatus } from '../../models/enums'
import UserContext, { Roles } from '../../UserContext'
import { displayDate } from '../../utilities'
import { ApiFetch } from '../Common/Api'
import { ServicesFilter, SearchOpportunitiesFilter, ExpiredUnassignedCriteria } from './types'
import * as QueryString from 'query-string'
import { navigate, useLocation } from '@reach/router'
import { SaveEvent, ClickEvent, ModalChangeEvent } from '../Common/AnalyticsEvents'
import { countActiveFilters } from '../Common/Utility'
import type { SortTuple } from '../Common/Table'
import {
    DEFAULT_SERVICE_REQUEST_TABS,
    MARKET_ADMIN_SERVICE_REQUEST_TABS,
    PROVIDER_SERVICE_REQUEST_TABS,
    WAIVER_SERVICE_REQUEST_TABS
} from './constants'

type ServiceRequestContextState = {
    services: ServiceRequestService[]
    setServices: React.Dispatch<React.SetStateAction<ServiceRequestService[]>>
    selectedService?: ServiceRequestService
    setSelectedService: React.Dispatch<ServiceRequestService>
    loading: boolean
    error?: string
    filterValues: ServicesFilter
    setFilterValues: React.Dispatch<React.SetStateAction<ServicesFilter>>
    readOnly: boolean
    isPopoutOpen: boolean
    setIsPopoutOpen: React.Dispatch<boolean>
    refreshList: () => void
    resendServiceReferral: (serviceRequestServiceID: number) => Promise<ServiceResendResponse>
    resendingServiceID?: number
    onViewService: (service: ServiceRequestService) => void
    handleCreate: () => void
    totalRows: number
    filterTags: string[]
    activeFilterTag: string
    setActiveFilterTag: React.Dispatch<React.SetStateAction<string>>
    filterCount: number
    isSearchOpportunities: boolean
    setIsSearchOpportunities: React.Dispatch<React.SetStateAction<boolean>>
    searchOpportunityFilters: SearchOpportunitiesFilter
    setSearchOpportunityFilters: React.Dispatch<React.SetStateAction<SearchOpportunitiesFilter>>
    searchOpportunitiesTotalRows: number
    hasSearchOpportunitySelections: boolean
    expiredUnassignedCriteria: ExpiredUnassignedCriteria
    applyExpiredUnassignedCriteria: (criteria: ExpiredUnassignedCriteria) => void
    resetExpiredUnassignedCriteria: () => void
}

export const ServiceRequestContext = React.createContext<ServiceRequestContextState>({} as ServiceRequestContextState)

const defaultSearchOpportunityFilters: SearchOpportunitiesFilter = {
    countiesState: [],
    locationText: '',
    serviceType: [],
    startDate: null,
    endDate: null,
    status: ['All']
}

const defaultStandardSort: SortTuple = ['startDate', 'desc']
const defaultExpiredUnassignedSort: SortTuple = ['startDate', 'asc']

export const createDefaultExpiredUnassignedCriteria = (): ExpiredUnassignedCriteria => ({
    authorizationID: undefined,
    memberID: undefined,
    specialties: [],
    countiesState: []
})

const normalizeExpiredUnassignedCriteria = (criteria: ExpiredUnassignedCriteria): ExpiredUnassignedCriteria => ({
    authorizationID: criteria.authorizationID?.trim() || undefined,
    memberID: criteria.memberID?.trim() || undefined,
    specialties: [...(criteria.specialties || [])],
    countiesState: [...(criteria.countiesState || [])]
})

const mapExpiredUnassignedCriteriaToFilters = (baseFilters: ServicesFilter, criteria: ExpiredUnassignedCriteria): ServicesFilter => {
    const sort = baseFilters.filterGroup === 'ExpiredUnassigned' && baseFilters.sort?.length
        ? baseFilters.sort
        : [defaultExpiredUnassignedSort]

    return {
        ...baseFilters,
        rowIndex: 0,
        filterGroup: 'ExpiredUnassigned',
        sort,
        authorizationID: criteria.authorizationID,
        serviceRequestMemberID: undefined,
        memberID: criteria.memberID,
        status: undefined,
        lastNonDraftStatus: undefined,
        specialties: [...criteria.specialties],
        counties: [],
        countiesState: criteria.countiesState.length > 0 ? criteria.countiesState.join(',') : undefined,
        createdByUserID: undefined,
        startDate: undefined,
        endDate: undefined,
        isSearchOpportunities: false,
        searchState: undefined,
        searchLocation: undefined,
        searchServiceType: undefined,
        searchStatus: undefined,
    }
}

const hasSearchSelections = (filters: SearchOpportunitiesFilter): boolean =>
    !!filters.countiesState?.length ||
    !!filters.serviceType?.length ||
    !!filters.startDate ||
    !!filters.endDate ||
    !!filters.locationText?.trim() ||
    !!filters.status?.some((s) => s !== 'All')

const parseResendFailure = async (error: unknown): Promise<ServiceResendResponse | undefined> => {
    if (!(error instanceof Response))
        return undefined

    try {
        return await error.json() as ServiceResendResponse
    } catch {
        return undefined
    }
}

const filterCountKeys = [
    'counties',
    'countiesState',
    'createdByUserID',
    'endDate',
    'memberID',
    'serviceRequestMemberID',
    'specialties',
    'startDate',
    'authorizationID'
]

export const ServiceRequestContextProvider: React.FC = ({ children }) => {
    const [services, setServices] = useState<ServiceRequestService[]>([])
    const [selectedService, setSelectedService] = useState<ServiceRequestService>()
    const [isPopoutOpen, setIsPopoutOpen] = useState<boolean>(false)
    const [readOnly, setReadOnly] = useState<boolean>(true)
    const [loading, setLoading] = useState(true)
    const [resendingServiceID, setResendingServiceID] = useState<number>()
    const [error, setError] = useState<string>()
    const { user, user: { userID }, hasAnyRole } = useContext(UserContext)
    const { search } = useLocation()
    const [totalRows, setTotalRows] = useState<number>(0)
    const [filterValues, setFilterValues] = useState<ServicesFilter>({
        pageSize: 10,
        rowIndex: 0,
        specialties: [],
        counties: [],
        countiesState: undefined,
        status: 'Active',
        filterGroup: 'Open',
        startDate: undefined,
        endDate: undefined,
        sort: [defaultStandardSort],
    })
    const [isSearchOpportunities, setIsSearchOpportunities] = useState<boolean>(false)
    const [searchOpportunityFilters, setSearchOpportunityFilters] = useState<SearchOpportunitiesFilter>(defaultSearchOpportunityFilters)
    const [searchOpportunitiesTotalRows, setSearchOpportunitiesTotalRows] = useState<number>(0)
    const [expiredUnassignedCriteria, setExpiredUnassignedCriteria] = useState<ExpiredUnassignedCriteria>(() => createDefaultExpiredUnassignedCriteria())

    const filterTags = useMemo(() => {
        if (hasAnyRole(Roles.Provider)) {
            return PROVIDER_SERVICE_REQUEST_TABS
        } else if (hasAnyRole(Roles.WaiverServiceCoordinator, Roles.WaiverServiceLeader)) {
            return WAIVER_SERVICE_REQUEST_TABS
        } else if (hasAnyRole(Roles.MarketAdmin)) {
            return MARKET_ADMIN_SERVICE_REQUEST_TABS
        } else {
            return DEFAULT_SERVICE_REQUEST_TABS
        }
    }, [hasAnyRole])

    const applyExpiredUnassignedCriteria = useCallback((criteria: ExpiredUnassignedCriteria) => {
        const normalizedCriteria = normalizeExpiredUnassignedCriteria(criteria)
        setExpiredUnassignedCriteria(normalizedCriteria)
        setFilterValues((currentFilters) => mapExpiredUnassignedCriteriaToFilters(currentFilters, normalizedCriteria))
    }, [])

    const resetExpiredUnassignedCriteria = useCallback(() => {
        applyExpiredUnassignedCriteria(createDefaultExpiredUnassignedCriteria())
    }, [applyExpiredUnassignedCriteria])

    // Set initial activeFilterTag to the first tag
    const [activeFilterTag, setActiveFilterTag] = useState<string>(filterTags[0])

    // If filterTags changes and current activeFilterTag is not in the list, update it
    useEffect(() => {
        if (!filterTags.some((tab) => tab === activeFilterTag)) {
            setActiveFilterTag(filterTags[0])
        }
    }, [activeFilterTag, filterTags])

    useEffect(() => {
        setLoading(true)
        //Query string does not format dates in a good way for the API
        const filters = { ...filterValues }

        if (isSearchOpportunities) {
            const selectedStatus = searchOpportunityFilters.status?.filter((s) => s !== 'All') || []
            filters.filterGroup = 'All'
            filters.status = undefined
            filters.lastNonDraftStatus = undefined
            filters.authorizationID = undefined
            filters.memberID = undefined
            filters.createdByUserID = undefined
            filters.groupCode = undefined
            filters.countiesState = undefined
            filters.counties = undefined
            filters.specialties = undefined
            filters.specialtiesFacets = undefined
            filters.isSearchOpportunities = true
            filters.searchState = searchOpportunityFilters.countiesState?.length ? searchOpportunityFilters.countiesState.join(',') : undefined
            filters.searchLocation = searchOpportunityFilters.locationText
            filters.searchServiceType = searchOpportunityFilters.serviceType?.length ? searchOpportunityFilters.serviceType.join(',') : undefined
            filters.searchStatus = selectedStatus.length ? selectedStatus.join(',') : 'All'
            filters.startDate = searchOpportunityFilters.startDate || undefined
            filters.endDate = searchOpportunityFilters.endDate || undefined
        } else {
            filters.isSearchOpportunities = false
            filters.searchState = undefined
            filters.searchLocation = undefined
            filters.searchServiceType = undefined
            filters.searchStatus = undefined

            if (filters.filterGroup === 'ExpiredUnassigned') {
                filters.createdByUserID = undefined
                filters.counties = []
                filters.startDate = undefined
                filters.endDate = undefined
                if (!filters.sort || filters.sort.length === 0) {
                    filters.sort = [defaultExpiredUnassignedSort]
                }
            }
        }

        // Format dates for API
        filters.startDate = filters.startDate && displayDate(filters.startDate)
        filters.endDate = filters.endDate && displayDate(filters.endDate)

        // Ensure specialties is always an array
        if (filters.specialties && !Array.isArray(filters.specialties)) {
            filters.specialties = [filters.specialties]
        }
        // If specialties is undefined, set to empty array
        if (!filters.specialties) {
            filters.specialties = []
        }
        if (filters.authorizationID === '') {
            filters.authorizationID = undefined
        }

        ApiFetch<Pageable<ServiceRequestService>>('ServiceRequestService', filters)
            .then(
                ({ results, totalRows }) => {
                    setServices(results)
                    setTotalRows(totalRows)
                    setLoading(false)
                },
                (error: string) => {
                    setError(error)
                    setLoading(false)
                }
            )
    }, [filterValues, isSearchOpportunities, searchOpportunityFilters])

    const buildSearchOpportunitiesApiFilters = (
        filters: ServicesFilter,
        defaultFilters: SearchOpportunitiesFilter
    ): ServicesFilter => ({
        ...filters,
        filterGroup: 'All',
        status: undefined,
        lastNonDraftStatus: undefined,
        authorizationID: undefined,
        createdByUserID: undefined,
        groupCode: undefined,
        countiesState: undefined,
        counties: undefined,
        specialties: undefined,
        specialtiesFacets: undefined,
        isSearchOpportunities: true,
        searchState: undefined,
        searchLocation: defaultFilters.locationText,
        searchServiceType: undefined,
        searchStatus: 'All',
        startDate: defaultFilters.startDate || undefined,
        endDate: defaultFilters.endDate || undefined,
        rowIndex: 0,
        pageSize: 1,
    })

    useEffect(() => {
        if (!isSearchOpportunities) return

        const filters = buildSearchOpportunitiesApiFilters(filterValues, defaultSearchOpportunityFilters)
        ApiFetch<Pageable<ServiceRequestService>>('ServiceRequestService', filters)
            .then(
                ({ totalRows }) => {
                    setSearchOpportunitiesTotalRows(totalRows)
                },
                () => {
                    setSearchOpportunitiesTotalRows(0)
                }
            )
    }, [isSearchOpportunities, filterValues])

    const refreshList = useCallback(() => {
        setFilterValues({ ...filterValues })
    }, [filterValues])

    const resendServiceReferral = useCallback(async (serviceRequestServiceID: number): Promise<ServiceResendResponse> => {
        setResendingServiceID(serviceRequestServiceID)

        try {
            const resendResult = await ApiFetch<ServiceResendResponse>(`ServiceRequestService/NotifyAndExtend?serviceRequestServiceID=${serviceRequestServiceID}`)

            if (resendResult.success) {
                const activeStatus: ServiceStatus = 'Active'
                const shouldRemoveFromExpiredUnassigned = !isSearchOpportunities && filterValues.filterGroup === 'ExpiredUnassigned'

                setServices((currentServices) => {
                    const updatedServices = currentServices.map((service) =>
                        service.serviceRequestServiceID === serviceRequestServiceID
                            ? {
                                ...service,
                                status: activeStatus,
                                statusDisplay: 'Active',
                                expiresOn: resendResult.expiresOn,
                                resendCount: resendResult.resendCount,
                                lastResentOnUtc: resendResult.expiresOn,
                                lastResentByUserID: userID,
                                lastResendOutcome: 'success'
                            }
                            : service
                    )

                    if (!shouldRemoveFromExpiredUnassigned)
                        return updatedServices

                    return updatedServices.filter((service) => service.serviceRequestServiceID !== serviceRequestServiceID)
                })
            }

            return resendResult
        } catch (error) {
            const parsedFailure = await parseResendFailure(error)
            if (parsedFailure)
                return parsedFailure

            return {
                serviceRequestServiceID,
                emails: [],
                resendCount: 0,
                success: false,
                failureReason: 'unknown_error'
            }
        } finally {
            setResendingServiceID(undefined)
        }
    }, [setServices, userID, filterValues.filterGroup, isSearchOpportunities])

    const onViewService = useCallback((service: ServiceRequestService) => {
        if (!service) return

        SaveEvent(ClickEvent(user, 'Service Request', 'Row', service.serviceRequestServiceID?.toString()))
        SaveEvent(ModalChangeEvent(user, 'Service Posting', 'Open'))
        setSelectedService(service)
        setIsPopoutOpen(true)
        setReadOnly(hasAnyRole(Roles.WaiverServiceLeader, Roles.MarketAdmin) ? false : userID !== service.createdBy)

        const q = QueryString.stringify({ ...QueryString.parse(search), id: service.serviceRequestServiceID })
        // eslint-disable-next-line no-console
        navigate(`service-requests?${q}`).catch(console.log)
    }, [hasAnyRole, userID, search, user])

    useEffect(() => {
        if (!search) return

        const queryParams = QueryString.parse(search)
        const idParam = queryParams['id']
        const id = parseInt(Array.isArray(idParam) ? idParam[0] : (idParam as string), 10)

        if (isNaN(id) || !id) return
        if (id === selectedService?.serviceRequestServiceID) {
            if (!isPopoutOpen && selectedService) {
                SaveEvent(ModalChangeEvent(user, 'Service Posting', 'Open'))
                setIsPopoutOpen(true)
                setReadOnly(hasAnyRole(Roles.WaiverServiceLeader, Roles.MarketAdmin) ? false : userID !== selectedService.createdBy)
            }

            return
        }

        const getService = async () => {
            const existing = services.find(b => b.serviceRequestServiceID === id)
            if (existing) return existing

            const byServiceId = await ApiFetch<{ results: ServiceRequestService[] }>('ServiceRequestService', {
                serviceRequestServiceID: id,
                pageSize: 1,
                rowIndex: 0
            })
            if (byServiceId.results.length > 0) return byServiceId.results[0]

            const byAuthorizationId = await ApiFetch<{ results: ServiceRequestService[] }>('ServiceRequestService', {
                authorizationID: id.toString(),
                pageSize: 1,
                rowIndex: 0
            })
            return byAuthorizationId.results[0]
        }

        // eslint-disable-next-line no-console
        getService().then((service) => {
            if (!service) {
                const query = QueryString.stringify({ ...queryParams, id: null, notificationID: null })
                void navigate(query ? `/service-requests?${query}` : '/service-requests')
                return
            }

            onViewService(service)
        }).catch(console.log)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit isPopoutOpen so closing the pane doesn't retrigger this effect and reopen it
    }, [onViewService, services, search, selectedService, hasAnyRole, userID])

    const handleCreate = useCallback(() => {
        setIsPopoutOpen(true)
        setSelectedService(undefined)
        setReadOnly(false)
    }, [])

    const keysToCheck = [
        'counties',
        'countiesState',
        'createdByUserID',
        'endDate',
        'memberID',
        'serviceRequestMemberID',
        'specialties',
        'startDate',
        'authorizationID',
        'status'
    ];
    
    const [filterCount, setFilterCount] = useState<number>(() => countActiveFilters(filterValues, keysToCheck));

    useEffect(() => {
        setFilterCount(countActiveFilters(filterValues, filterCountKeys))
    }, [filterValues])


    return <ServiceRequestContext.Provider
        children={children}
        value={{
            services,
            setServices,
            selectedService,
            setSelectedService,
            readOnly,
            loading,
            error,
            filterValues,
            setFilterValues,
            refreshList,
            resendServiceReferral,
            resendingServiceID,
            onViewService,
            handleCreate,
            isPopoutOpen,
            setIsPopoutOpen,
            totalRows,
            filterTags,
            activeFilterTag,
            setActiveFilterTag,
            filterCount,
            isSearchOpportunities,
            setIsSearchOpportunities,
            searchOpportunityFilters,
            setSearchOpportunityFilters,
            searchOpportunitiesTotalRows,
            hasSearchOpportunitySelections: hasSearchSelections(searchOpportunityFilters),
            expiredUnassignedCriteria,
            applyExpiredUnassignedCriteria,
            resetExpiredUnassignedCriteria
        }}
    />
}
